/**
 * Where past videos open (Kazzy, 16 Oct 2026: their home is Artifacts, not Review): the list is Artifacts' Past videos
 * tab (`/artifacts?kind=past_videos`, narrowed by brief or by creator), and each group its own page
 * (`/past-videos/<setId>`, with `?video=<videoId>` to open one video). Brief memory's reasoning rows use `letGoHref`: a
 * let-go her team gave on a past video opens that video in its group, never Review Content (a past video is not in her
 * list there). Pure; strings, so any page can link here before the route exists in its build.
 */

export interface PastVideoPlace {
  setId: string;
  videoId?: string | null;
}

/** Artifacts' tab for past videos (`?kind=`). */
export const PAST_VIDEOS_TAB = 'past_videos';

/**
 * The tab's narrowing in its address: `brief` (a brief's document id) and `creatorKey` (`<platform>:<platformId>`, as
 * the creator profile opens). Not `creator`: that one opens the creator profile panel on any page.
 */
export const PAST_VIDEOS_BRIEF_PARAM = 'brief';
export const PAST_VIDEOS_CREATOR_PARAM = 'creatorKey';
/**
 * Score round 7 (E2): beside `brief`, one applied brief change (its suggestion's id), so the tab shows only the groups
 * whose answers led to it (Brief changes' "Your team’s answers on past videos led to this change."). Here, not in
 * `tab-search.ts` (which reads it): this file is the one both import.
 */
export const PAST_VIDEOS_CHANGE_PARAM = 'change';

/** `/past-videos/<setId>`, plus `?video=<videoId>` to open that video. */
export const pastVideoHref = ({ setId, videoId }: PastVideoPlace): string =>
  `/past-videos/${encodeURIComponent(setId)}${videoId ? `?video=${encodeURIComponent(videoId)}` : ''}`;

/**
 * Artifacts' Past videos tab, narrowed to one brief and/or one creator when given; on a brief, to one change of it too
 * (score round 7, E2: `change`, the groups whose answers led to it). A change without its brief isn't sent.
 */
export function pastVideosTabHref({ brief, creator, change }: { brief?: string | null; creator?: string | null; change?: string | null } = {}): string {
  const q = new URLSearchParams({ kind: PAST_VIDEOS_TAB });
  if (brief) q.set(PAST_VIDEOS_BRIEF_PARAM, brief);
  if (brief && change) q.set(PAST_VIDEOS_CHANGE_PARAM, change);
  if (creator) q.set(PAST_VIDEOS_CREATOR_PARAM, creator);
  return `/artifacts?${q.toString()}`;
}

/** A let-go from brief memory's reasoning: its past video's page when it came from one, else null (a real draft). */
export const letGoHref = (letGo: { pastVideo?: { setId: string; videoId: string } | null }): string | null =>
  letGo.pastVideo ? pastVideoHref(letGo.pastVideo) : null;

// ── The way back to the tab as she left it (C2, E2) ─────────────────────────────────────────────────────────────────

declare module '@tanstack/history' {
  interface HistoryState {
    /** The Past videos tab's address search as she left it for a group (`?kind=past_videos&brief=…`), for "← Artifacts". */
    pastTab?: string;
    /**
     * Round 5 (K0, C4 and E1): the group she is coming back from ("← Artifacts", the phone's round ‹), so the tab or All
     * brings its card into view and gives its link the keyboard (`[data-set-id]`).
     */
    pastFrom?: string;
  }
}

/** The history state a group's link carries from the tab (`search`: the tab's own, `?kind=past_videos…`). Pure. */
export const pastTabState = (search: string): { pastTab: string } => ({ pastTab: search });

/**
 * The tab's search to go back to, from a history entry's state: only one that is the Past videos tab's own
 * (`?kind=past_videos`, then nothing or `&…`) or All's (an Artifacts search with no `kind`, or `kind=all`: score round 4,
 * E1, a card opened from All goes back to All), so no other state ever sends her elsewhere; else null. Pure.
 */
export function readPastTab(state: unknown): string | null {
  const tab = state && typeof state === 'object' ? (state as { pastTab?: unknown }).pastTab : null;
  if (typeof tab !== 'string' || !tab.startsWith('?')) return null;
  const own = `?kind=${PAST_VIDEOS_TAB}`;
  if (tab === own || tab.startsWith(`${own}&`)) return tab;
  const kind = new URLSearchParams(tab).get('kind');
  return kind == null || kind === 'all' ? tab : null;
}

// ── The card she came back from (score round 5, K0: C4 sends it, E1 lands on it) ──────────────────────────────────────

/** A group's id as the state may carry it: what ids and test ids are made of, never a selector's quotes or brackets. */
const SET_ID = /^[A-Za-z0-9_-]{1,64}$/;

/** The history state "← Artifacts" carries back from a group (`setId`), beside the tab's own search (`pastTab`). Pure. */
export const pastFromState = (setId: string): { pastFrom: string } => ({ pastFrom: setId });

/**
 * The group she came back from, from a history entry's state (`pastFromState`): its id when it reads as one (letters,
 * digits, `-` and `_`, up to 64), so it is safe in `[data-set-id="…"]`; else null. Pure.
 */
export function readPastFrom(state: unknown): string | null {
  const from = state && typeof state === 'object' ? (state as { pastFrom?: unknown }).pastFrom : null;
  return typeof from === 'string' && SET_ID.test(from) ? from : null;
}
